import React, { useState, useEffect } from 'react';
import { Check, Star, Zap, Crown, Loader } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionPlans = ({ onClose }) => {
  const { isAuthenticated } = useAuth();
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/subscription-plans`);
      const data = await response.json();
      setPlans(data.plans || {});
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!isAuthenticated) {
      alert('Please sign in to subscribe');
      return;
    }

    setProcessingPlan(planId);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/subscription/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin,
        }),
      });

      const data = await response.json();
      
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      alert('Failed to start subscription. Please try again.');
    } finally {
      setProcessingPlan(null);
    }
  };

  const getPlanIcon = (planId) => {
    if (planId.includes('premium')) return <Crown className="text-yellow-500" size={24} />;
    return <Star className="text-red-500" size={24} />;
  };

  const getDisplayPlans = () => {
    return Object.entries(plans).filter(([key]) => 
      billingCycle === 'monthly' ? key.includes('monthly') : key.includes('yearly')
    );
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
        <div className="bg-gray-900 rounded-xl p-8">
          <Loader className="animate-spin text-red-500 mx-auto" size={32} />
          <p className="text-gray-400 mt-4">Loading plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gray-900 rounded-2xl max-w-4xl w-full p-8 relative" data-testid="subscription-plans">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white text-2xl"
        >
          ×
        </button>

        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">Choose Your Plan</h2>
          <p className="text-gray-400">Unlock premium content and features</p>
        </div>

        {/* Billing Cycle Toggle */}
        <div className="flex justify-center mb-8">
          <div className="bg-gray-800 rounded-full p-1 flex">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-red-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                billingCycle === 'yearly'
                  ? 'bg-red-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Yearly
              <span className="ml-2 text-xs bg-green-600 px-2 py-0.5 rounded-full">Save 17%</span>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {getDisplayPlans().map(([planId, plan]) => (
            <div
              key={planId}
              className={`relative rounded-xl border-2 p-6 transition-all ${
                planId.includes('premium')
                  ? 'border-yellow-500 bg-gradient-to-b from-yellow-500/10 to-transparent'
                  : 'border-gray-700 hover:border-gray-600'
              }`}
            >
              {planId.includes('premium') && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-500 text-black text-xs font-bold px-3 py-1 rounded-full">
                  MOST POPULAR
                </div>
              )}

              <div className="flex items-center gap-3 mb-4">
                {getPlanIcon(planId)}
                <h3 className="text-xl font-bold text-white">{plan.name}</h3>
              </div>

              <div className="mb-6">
                <span className="text-4xl font-bold text-white">${plan.price}</span>
                <span className="text-gray-400">/{plan.interval}</span>
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-gray-300">
                    <Check size={18} className="text-green-500 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSubscribe(planId)}
                disabled={processingPlan === planId}
                className={`w-full py-3 rounded-lg font-bold transition-colors flex items-center justify-center gap-2 ${
                  planId.includes('premium')
                    ? 'bg-yellow-500 hover:bg-yellow-600 text-black'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                } disabled:opacity-50`}
              >
                {processingPlan === planId ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Zap size={18} />
                    Subscribe Now
                  </>
                )}
              </button>
            </div>
          ))}
        </div>

        <p className="text-center text-gray-500 text-sm mt-6">
          Cancel anytime. No hidden fees.
        </p>
      </div>
    </div>
  );
};

export default SubscriptionPlans;
